<template lang="jade">
  div.Input(class="Input--date"
            ":tooltip"="$parent.fdata.tip || false"
            v-bind:class="{'Input--filled': formInput, 'Input--invalid': error, 'Input--valid': complete }"
            ":skin"="skins.inputs")
    input.Input__field(type="text"
                       ":disabled"="disabled"
                       @keydown.tab="tabBefore"
                       @keyup.tab="tabAfter"
                       v-model="formInput | date"
                       autocomplete="off"
                       readonly="true"
                       ":required"="$parent.required"
                       @click="show = !show"
                       @focus="attempt++")
    label.Input__label
      span.Input__label-content
        i.fa.fa-calendar(style="margin-right: 10px")
        {{ label }}
    lightbox(":open.sync"="show"
             width="360"
             ":callback"="prompt"
             ":loading"="loading")
      include ./_partials/calendar.jade
</template>

<script>
  import Moment from 'moment'
  import _ from 'lodash'
  import { skins, forms } from 'src/vuex/getters'
  import { addNotification, removeNotification } from 'src/vuex/actions'
  import lightbox from 'src/components/dialogs/lightbox'

  export default {
    props: {
      width: {
        type: String,
        default: '300px'
      },
      disabled: {
        type: Boolean,
        default: false
      },
      value: {
        type: String,
        default: ''
      },
      name: {
        default: ''
      },
      dformat: {
        type: String,
        default: 'D MMM YYYY'
      },
      constraints: {
        type: Object,
        coerce: x => {
          if (x.min === undefined) {
            x.min = new Date(Moment().subtract(100, 'Years').format())
          }
          if (x.max === undefined) {
            x.max = new Date(Moment().add(20, 'Years').format())
          }
          return x
        }
      },
      label: {
        type: String,
        default: 'Date Picker'
      },
      year: {
        type: Number,
        default: Moment().year()
      },
      month: {
        type: Number,
        default: Moment().month()
      },
      idx: {
        type: Number,
        default: 0
      }
    },
    data () {
      return {
        show: false,
        min: null,
        max: null,
        week: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
        months: Moment.months(),
        date: [],
        uid: this.$parent.uid,
        // now: new Date(),
        // thisDate: new Date(),
        // dateClick: new Date().getFullYear(),
        showMonth: true,
        flow: '',
        display: 'days',
        formInput: '',
        moment: Moment(),
        attempts: 0,
        error: false,
        complete: false,
        loading: false
      }
    },
    components: {
      lightbox
    },
    filters: {
      outOfRange (d, constraint) {
        if (_.size(this.constraints) === 0) return true
        let date = new Date(d.year, d.month, 1)
        switch (constraint) {
          case 'before':
            return date.getTime() > this.constraints.max.getTime()
          case 'after':
            return date.getTime() < this.constraints.min.getTime()
          default:
            return true
        }
      },
      date: {
        read (val) {
          return val ? Moment(val).format('DD/MM/YYYY') : ''
        },
        write (val) {
          return val ? Moment(val).format('D MMM YYYY') : ''
        }
      }
    },
    events: {
      'clear': 'reset',
      'set': 'insert',
      'force::validate' () {
        this.attempts = 1
        this.validate()
      },
      'external::error' (errors) {
        this.$set('error', !_.isNull(errors[this.name]))
      }
    },
    computed: {
      years () {
        let limit = this.constraints
        return _.size(limit) > 0 ? _.range(limit.min.getFullYear(), limit.max.getFullYear() + 1)
                                 : _.range(1929, 2100)
      },

      calendar () {
        // let date = new Date(this.year, this.month, 1)
        var currentMoment = this.moment
        let vm = this
        let moment = Moment([this.year, this.month, 1])

        let month = _.assign(this.getMonth(moment), {
          next: this.getMonth(moment.clone().add(1, 'month')),
          previous: this.getMonth(moment.clone().subtract(1, 'month'))
        })

        let daysInMonth = _.curry((moment, n) => {
          if ((n + 1) < moment.first) {
            return _.assign({}, moment.options, { value: moment.previous.daysInMonth - (moment.first - (n + 2)) })
          }

          if (n >= (moment.daysInMonth + (moment.first - 1))) {
            return _.assign({}, moment.options, { value: n - ((moment.first - 2) + moment.daysInMonth) })
          }

          if ((n + 1) >= moment.first && n < moment.daysInMonth + (moment.first - 1)) {
            let value = (n + 2) - moment.first
            let date = Moment([moment.year, moment.month, value])
            let active = currentMoment.clone().format(vm.dformat) === date.clone().format(vm.dformat)
            let disabled = this.isDisabled(date)
            return _.assign({}, moment.options, {
              value,
              active,
              disabled,
              inMonth: true,
              date
            })
          }
        })(month)

        return _.set(month, 'days', _.times(42, daysInMonth))
      }
    },
    vuex: {
      getters: {
        skins,
        forms
      },
      actions: {
        addNotification,
        removeNotification
      }
    },
    methods: {
      reset () {
        this.$set('formInput', '')
        return true
      },

      insert (data) {
        this.$set('formInput', data[this.name])
        return true
      },

      tabBefore () {
        this.show = false
      },

      tabAfter () {
        this.show = true
      },

      validate () {
        let validated = this.computedValidate(this.$parent.fdata, this.formInput)
        this.attempts > 0 && validated ? this.setError() : this.setComplete()
        this.fieldState(validated)
        this.notice('error', validated)
      },

      isDisabled (d) {
        let ts = new Date(d).getTime()
        let constraints = this.constraints
        if (_.size(constraints) > 0) {
          let {min, max} = constraints
          return ts > max.getTime() || ts < min.getTime()
        }
        return false
      },

      notice (type, valid) {
        _.isNil(valid) || _.isEmpty(valid)
          ? this.removeNotification(this.name)
          : this.addNotification({
            type: type,
            msg: valid,
            id: this.name,
            show: true
          })

        this.$parent.formInput = this.formInput

        this.$dispatch('fdata::input', {
          [this.name]: this.formInput
        }, this.idx)
      },

      fieldState (valid) {
        if (this.attempts < 1) return false
        _.isNil(valid) ? this.setFieldComplete() : this.setFieldError()
      },

      setFieldComplete () {
        this.$set('complete', true)
        this.$set('error', false)
      },

      setFieldError () {
        this.$set('error', true)
        this.$set('complete', false)
      },

      setError () {
        this.error = true
        this.complete = false
      },

      setComplete () {
        this.error = false
        this.complete = true
      },

      setDisplay (display) {
        this.$set('display', display)
        if (_.eq(display, 'years')) _.delay(this.setScrollPosition, 50)
      },

      setMonth (moment, flow) {
        let vm = this
        this.$set('flow', flow)
        this.$set('showMonth', false)
        this.$set('year', moment.year)
        this.$set('month', moment.month)
        _.delay(() => {
          vm.$set('showMonth', true)
        }, 400)
      },

      getMonth (moment) {
        let first = moment.day() > 0 ? moment.day() : 7
        let daysInMonth = moment.daysInMonth()
        return {
          year: moment.year(),
          month: moment.month(),
          daysInMonth: daysInMonth,
          first: first,
          last: (daysInMonth + (first - 1)),
          days: [],
          options: {
            inMonth: false,
            disabled: true
          }
        }
      },

      setMonthByPosition (month) {
        this.$set('display', 'days')
        this.$set('month', month)
      },

      setYearByPosition (year) {
        this.$set('display', 'days')
        this.$set('year', year)
      },

      setYear (flow, year) {
        this.$set('year', flow === 'add' ? year++ : year--)
      },

      prompt (result) {},

      setScrollPosition () {
        let container = document.getElementsByClassName('Calendar__years')
        let active = document.getElementsByClassName('Calendar__year--active')
        let offset = _.head(active).offsetTop - _.head(container).offsetTop
        _.head(container).scrollTop = offset
      },

      setDate (moment, index) {
        this.$set('moment', moment)
        this.$set('formInput', moment.format(this.dformat))
        this.validate()
        this.$set('show', false)
      }
    },
    created () {
      let vuex = _.find(this.forms, ['uid', this.uid])
      if (!_.includes(['TypeMultipart'], this.$parent._context.constructor.name)) {
        if (vuex) {
          let formData = vuex.data[this.name]
          if (formData) {
            this.formInput = _.isArray(formData) ? formData[this.idx] : formData
          } else {
            this.formInput = this.value
          }
        } else {
          this.formInput = this.value
        }
      } else {
        this.formInput = this.value
      }
    },
    ready () {
      this.validate()
      // if (_.size(this.constraints) > 0) {
      //   this.$set('year', 2000)
      //   this.$set('month', 9)
      // }
    }
  }
</script>



// WEBPACK FOOTER //
// picker.vue?32da5cdc